import { ApolloClient, ApolloLink, ApolloProvider, createHttpLink } from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { getUserTimezone } from '@mnd-frontend/dates';
import { Global } from '@mnd-frontend/ui';
import { getCache, getCookie } from '@mnd-frontend/utils';
import CreateDebug from 'debug';
import type React from 'react';
import { Suspense } from 'react';
import { BrowserRouter } from 'react-router';
import 'what-input';

const logNetworkError = CreateDebug('mnd:network:error');
const logGraphqlError = CreateDebug('mnd:graphql:error');

const authLink = new ApolloLink((operation, forward) => {
  const revokedAdminView = getCookie('revoked_admin_view');

  // add the authorization and locale to the headers
  operation.setContext((ctx: any) => ({
    ...ctx,
    headers: {
      ...ctx.headers,
      'Accept-Language': window.locale,
      'Accept-Timezone': window.timezone || getUserTimezone(),
      ...(revokedAdminView ? { 'revoked-admin-view': revokedAdminView } : {}),
    },
  }));

  return forward(operation);
});

const httpLink = createHttpLink({
  uri: '/graphql',
});

const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors)
    logGraphqlError(
      graphQLErrors.reduce(
        (out, { message, locations, path }) =>
          `${out}\n${message}, Location: ${JSON.stringify(locations, null, 2)}, Path: ${path}`,
        '',
      ),
    );

  if (networkError) logNetworkError(networkError);
});

const link = ApolloLink.from([errorLink, authLink, httpLink]);

const client = new ApolloClient({
  cache: getCache(),
  link,
  name: 'mnd-publish-frontend',
  version:
    (import.meta.env.VITE_BUILD_VERSION as string) ?? (import.meta.env.DEV ? 'local-dev' : ''),
});

export const Setup = ({ children }: { children: React.ReactNode }) => (
  <>
    <Global />
    <Suspense fallback={<div />}>
      <ApolloProvider client={client}>
        <BrowserRouter>{children}</BrowserRouter>
      </ApolloProvider>
    </Suspense>
  </>
);
